import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ShieldCheck, BadgeCheck, AlertTriangle, Loader2, Search, IdCard,
  Upload, ArrowRight, ArrowLeft, CheckCircle2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, homeForRole } from "@/store/AuthContext";

const UFS = ["SP", "RJ", "MG", "RS", "PR", "SC", "BA", "PE", "CE", "GO", "DF", "ES", "PA", "AM", "MT", "MS", "MA", "PB", "RN", "AL", "PI", "SE", "RO", "TO", "AC", "AP", "RR"];

// Mock da base do CFM
const CFM_DB = {
  "123456": { name: "Dr. Carlos Andrade Silva", specialty: "Clínica Médica", status: "ativo" },
  "234567": { name: "Dra. Marina Costa Ribeiro", specialty: "Dermatologia", status: "ativo" },
  "111111": { name: "—", specialty: "—", status: "suspenso" },
};

const STEPS = ["CRM", "Dados", "Identidade"];

export default function SoloOnboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const initialPlan = ["solo", "basic", "max"].includes(searchParams.get("plan")) ? searchParams.get("plan") : "solo";
  const [plan, setPlan] = useState(initialPlan);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Etapa 1 — CRM
  const [crm, setCrm] = useState("");
  const [uf, setUf] = useState("SP");
  const [crmChecking, setCrmChecking] = useState(false);
  const [crmResult, setCrmResult] = useState(null); // {ok, name, specialty, status}

  // Etapa 2 — Dados da conta
  const [form, setForm] = useState({ name: "", cpf: "", email: "", phone: "", specialty: "", password: "", confirm: "", modality: "ambos", price_presencial: "", price_online: "", cep: "", logradouro: "", bairro: "", cidade: "", cidadeUf: "", numero: "", complemento: "", coupon: "" });
  const mtoggle = (a) => `rounded-xl border px-2 py-2 text-xs font-semibold transition-colors ${a ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`;
  const handleCep = async (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 8);
    setForm((f) => ({ ...f, cep: v }));
    if (v.length === 8) {
      try {
        const r = await fetch(`https://viacep.com.br/ws/${v}/json/`);
        const d = await r.json();
        if (d.erro) return toast.error("CEP não encontrado.");
        setForm((f) => ({ ...f, logradouro: d.logradouro || "", bairro: d.bairro || "", cidade: d.localidade || "", cidadeUf: d.uf || "" }));
        toast.success("Endereço preenchido pelo CEP");
      } catch (err) {
        toast.error("Falha ao consultar o CEP.");
      }
    }
  };
  const composedAddress = (f) => (f.logradouro ? `${f.logradouro}, ${f.numero}${f.complemento ? ` - ${f.complemento}` : ""} - ${f.bairro}, ${f.cidade}/${f.cidadeUf} - CEP ${f.cep}` : "");

  // Etapa 3 — KYC (documento + IA)
  const [docName, setDocName] = useState("");
  const [docData, setDocData] = useState("");
  const [kycResult, setKycResult] = useState(null);
  const [kycProcessing, setKycProcessing] = useState(false);
  const [kycDone, setKycDone] = useState(false);

  const validarCRM = () => {
    if (!/^\d{4,7}$/.test(crm)) {
      setCrmResult({ ok: false, msg: "Informe um número de CRM válido (apenas dígitos)." });
      return;
    }
    setCrmChecking(true);
    setCrmResult(null);
    setTimeout(() => {
      setCrmResult({ ok: true, name: "", specialty: "" });
      toast.success("CRM registrado", { description: "Validaremos seu registro após a confirmação do pagamento." });
      setCrmChecking(false);
    }, 800);
  };

  const goDados = () => {
    if (!crmResult?.ok) return toast.error("Valide o CRM antes de continuar.");
    setStep(1);
  };

  const goIdentidade = () => {
    if (!form.name.trim()) return toast.error("Nome obrigatório.");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return toast.error("E-mail inválido.");
    if (!form.cpf.trim()) return toast.error("Informe o CPF.");
    if (form.password.length < 6) return toast.error("Senha deve ter ao menos 6 caracteres.");
    if (form.password !== form.confirm) return toast.error("As senhas não conferem.");
    const pres = plan === "solo" && (form.modality === "presencial" || form.modality === "ambos");
    const onl = plan === "solo" && (form.modality === "online" || form.modality === "ambos");
    if (pres && !(Number(form.price_presencial) > 0)) return toast.error("Informe o valor da consulta presencial.");
    if (pres && form.cep.replace(/\D/g, "").length !== 8) return toast.error("Informe um CEP válido para o atendimento presencial.");
    if (pres && !form.numero.trim()) return toast.error("Informe o número do endereço.");
    if (onl && !(Number(form.price_online) > 0)) return toast.error("Informe o valor da consulta online.");
    setStep(2);
  };

  const onDocFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDocName(file.name);
    setKycResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      const im = new Image();
      im.onload = () => {
        const max = 1280;
        let w = im.width, h = im.height;
        if (w > max || h > max) { const r = Math.min(max / w, max / h); w = Math.round(w * r); h = Math.round(h * r); }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(im, 0, 0, w, h);
        setDocData(canvas.toDataURL("image/jpeg", 0.85));
      };
      im.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const verificarDocumento = async () => {
    if (!docData) return toast.error("Envie a foto do seu documento (RG ou CNH).");
    if (form.cpf.replace(/\D/g, "").length !== 11) return toast.error("Volte e informe um CPF válido (11 dígitos).");
    setKycProcessing(true);
    setKycResult(null);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/solo/verify-document`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cpf: form.cpf, name: form.name, image_base64: docData }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Falha na verificação.");
      setKycResult(d);
      if (d.ok) { setKycDone(true); toast.success("Documento validado por IA", { description: d.reason }); }
      else { setKycDone(false); toast.error("Documento não validado", { description: d.reason }); }
    } catch (e) {
      toast.error(e.message || "Falha ao verificar o documento.");
    } finally {
      setKycProcessing(false);
    }
  };

  const finalizar = async () => {
    setSubmitting(true);
    try {
      await register({
        name: form.name,
        email: form.email,
        password: form.password,
        clinic_name: `Consultório ${form.name.split(" ")[0]}`,
        phone: form.phone,
        role: plan === "solo" ? "medico" : "admin",
        intended_plan: plan,
        specialty: form.specialty,
        crm,
        uf,
        modality: form.modality,
        price_presencial: Number(form.price_presencial) || 0,
        price_online: Number(form.price_online) || 0,
        address: composedAddress(form),
        coupon: form.coupon,
      });
      try {
        localStorage.setItem("veloclin_solo_profile", JSON.stringify({
          crm, uf, cpf: form.cpf, specialty: form.specialty,
          modality: form.modality, price_presencial: Number(form.price_presencial) || 0,
          price_online: Number(form.price_online) || 0, address: composedAddress(form),
          verified: true, twofa: true,
        }));
        localStorage.setItem("veloclin_plan", plan);
      } catch (e) {}
      toast.success("Conta VeloClin criada e verificada!", { description: "Teste gratuito de 14 dias iniciado." });
      navigate(homeForRole(plan === "solo" ? "medico" : "admin"), { replace: true });
    } catch (e) {
      toast.error(e.message || "Falha ao criar conta.");
      setStep(1);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] px-4 py-8">
      <div className="mx-auto max-w-2xl">
        {/* Marca */}
        <Link to="/" className="mb-6 flex items-center justify-center gap-2">
          <img src="/veloclin-logo.png" alt="VeloClin" data-testid="brand-logo" className="h-12 w-auto" />
          <span className="rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-bold text-blue-600">Solo</span>
        </Link>

        <div className="mb-2 text-center">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Cadastro Médico Verificado</h1>
          <p className="mt-1 text-sm text-slate-500">Barreira dupla anti-fraude: validação de CRM (CFM) + verificação de identidade (KYC).</p>
        </div>

        {/* Stepper */}
        <div className="mx-auto mb-6 mt-5 flex max-w-md items-center justify-between">
          {STEPS.map((s, i) => (
            <div key={s} className="flex flex-1 items-center">
              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors ${i < step ? "bg-emerald-500 text-white" : i === step ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"}`}>
                {i < step ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && <div className={`mx-1 h-0.5 flex-1 ${i < step ? "bg-emerald-500" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        <div className="mb-4 rounded-2xl border border-slate-200/70 bg-white p-3 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Plano escolhido · teste grátis de 14 dias</p>
          <div className="grid grid-cols-3 gap-2">
            {[{ id: "solo", n: "Solo", p: "R$ 149" }, { id: "basic", n: "Basic", p: "R$ 399" }, { id: "max", n: "Max", p: "R$ 899" }].map((pl) => (
              <button key={pl.id} type="button" data-testid={`choose-plan-${pl.id}`} onClick={() => setPlan(pl.id)} className={`rounded-xl border p-2 text-center transition-colors ${plan === pl.id ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <p className={`text-sm font-bold ${plan === pl.id ? "text-blue-700" : "text-slate-700"}`}>{pl.n}</p>
                <p className="text-[11px] text-slate-400">{pl.p}/mês</p>
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-[0_4px_20px_rgba(0,0,0,0.05)]">
          <AnimatePresence mode="wait">
            {/* ETAPA 1 — CRM */}
            {step === 0 && (
              <motion.div key="crm" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }}>
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><ShieldCheck className="h-5 w-5 text-blue-600" />Validação de CRM na Base Oficial (CFM)</h2>
                <p className="mt-1 text-sm text-slate-500">Informe seu CRM e a UF para validarmos o seu registro no Conselho.</p>
                <div className="mt-4 grid grid-cols-[1fr_110px] gap-3">
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Número do CRM</Label>
                    <Input data-testid="crm-number-input" value={crm} onChange={(e) => { setCrm(e.target.value.replace(/\D/g, "").slice(0, 7)); setCrmResult(null); }} inputMode="numeric" placeholder="Somente números" className="mt-1.5" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">UF</Label>
                    <Select value={uf} onValueChange={setUf}>
                      <SelectTrigger data-testid="crm-uf-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-60">{UFS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <button data-testid="validate-crm-btn" onClick={validarCRM} disabled={crmChecking} className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-3 text-sm font-bold text-white transition-colors hover:bg-slate-800 disabled:opacity-60">
                  {crmChecking ? <><Loader2 className="h-4 w-4 animate-spin" />Consultando base do CFM...</> : <><Search className="h-4 w-4" />Validar CRM na Base Oficial</>}
                </button>

                {crmResult?.ok && (
                  <div data-testid="crm-success-seal" className="mt-4 flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3.5">
                    <BadgeCheck className="h-6 w-6 shrink-0 text-emerald-600" />
                    <div className="text-sm">
                      <p className="font-bold text-emerald-800">{crmResult.name}</p>
                      <p className="text-emerald-700">{crmResult.specialty} · CRM {crm}-{uf} · <b>Ativo / Regular</b></p>
                    </div>
                  </div>
                )}
                {crmResult && !crmResult.ok && (
                  <div data-testid="crm-error-alert" className="mt-4 flex items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 p-3.5 text-sm text-rose-700">
                    <AlertTriangle className="h-5 w-5 shrink-0" />
                    <span>{crmResult.msg}</span>
                  </div>
                )}

                <div className="mt-5 flex justify-end">
                  <button data-testid="crm-continue-btn" onClick={goDados} disabled={!crmResult?.ok} className="flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400">
                    Continuar<ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* ETAPA 2 — DADOS */}
            {step === 1 && (
              <motion.div key="dados" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }}>
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><IdCard className="h-5 w-5 text-blue-600" />Seus dados profissionais</h2>
                <div className="mt-4 space-y-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field label="Nome completo" testid="solo-name-input" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                    <Field label="Especialidade" testid="solo-specialty-input" value={form.specialty} onChange={(v) => setForm({ ...form, specialty: v })} />
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field label="CPF" testid="solo-cpf-input" value={form.cpf} onChange={(v) => setForm({ ...form, cpf: v })} placeholder="000.000.000-00" />
                    <Field label="Telefone / WhatsApp" testid="solo-phone-input" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="(11) 90000-0000" />
                  </div>
                  <Field label="E-mail (login)" testid="solo-email-input" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="voce@clinica.com" />
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field label="Senha" testid="solo-password-input" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} />
                    <Field label="Confirmar senha" testid="solo-confirm-input" type="password" value={form.confirm} onChange={(v) => setForm({ ...form, confirm: v })} />
                  </div>
                  <Field label="Cupom de desconto (opcional)" testid="solo-coupon-input" value={form.coupon} onChange={(v) => setForm({ ...form, coupon: v.toUpperCase() })} placeholder="Ex: VELO50, VELO30D" />

                  {plan === "solo" && (
                  <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-4">
                    <Label className="text-xs font-semibold text-slate-600">Modalidade de atendimento</Label>
                    <div className="mt-1.5 grid grid-cols-3 gap-2">
                      <button type="button" data-testid="modality-presencial" onClick={() => setForm({ ...form, modality: "presencial" })} className={mtoggle(form.modality === "presencial")}>Só presencial</button>
                      <button type="button" data-testid="modality-online" onClick={() => setForm({ ...form, modality: "online" })} className={mtoggle(form.modality === "online")}>Só online</button>
                      <button type="button" data-testid="modality-ambos" onClick={() => setForm({ ...form, modality: "ambos" })} className={mtoggle(form.modality === "ambos")}>Ambos</button>
                    </div>

                    {(form.modality === "online" || form.modality === "ambos") && (
                      <div className="mt-3">
                        <Label className="text-xs font-semibold text-slate-600">Valor da consulta online</Label>
                        <div className="relative mt-1.5">
                          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">R$</span>
                          <Input data-testid="price-online-input" type="number" value={form.price_online} onChange={(e) => setForm({ ...form, price_online: e.target.value })} className="pl-9" placeholder="200,00" />
                        </div>
                      </div>
                    )}
                    {(form.modality === "presencial" || form.modality === "ambos") && (
                      <div className="mt-3 space-y-3">
                        <div>
                          <Label className="text-xs font-semibold text-slate-600">Valor da consulta presencial</Label>
                          <div className="relative mt-1.5">
                            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">R$</span>
                            <Input data-testid="price-presencial-input" type="number" value={form.price_presencial} onChange={(e) => setForm({ ...form, price_presencial: e.target.value })} className="pl-9" placeholder="300,00" />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs font-semibold text-slate-600">CEP do local de atendimento</Label>
                          <Input data-testid="cep-input" value={form.cep} onChange={handleCep} inputMode="numeric" placeholder="00000-000" className="mt-1.5" />
                          <p className="mt-1 text-[11px] text-slate-400">Digite o CEP e preenchemos o endereço automaticamente.</p>
                        </div>
                        {form.logradouro && (
                          <div data-testid="addr-autofill" className="grid grid-cols-2 gap-2 rounded-lg border border-slate-100 bg-white p-3 text-xs text-slate-600">
                            <p data-testid="addr-logradouro"><span className="text-slate-400">Logradouro:</span> {form.logradouro}</p>
                            <p><span className="text-slate-400">Bairro:</span> {form.bairro}</p>
                            <p><span className="text-slate-400">Cidade:</span> {form.cidade}</p>
                            <p><span className="text-slate-400">UF:</span> {form.cidadeUf}</p>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                          <Field label="Número" testid="numero-input" value={form.numero} onChange={(v) => setForm({ ...form, numero: v })} placeholder="123" />
                          <Field label="Complemento" testid="complemento-input" value={form.complemento} onChange={(v) => setForm({ ...form, complemento: v })} placeholder="Sala 2 (opcional)" />
                        </div>
                      </div>
                    )}
                  </div>
                  )}
                </div>
                <div className="mt-5 flex justify-between">
                  <BackBtn onClick={() => setStep(0)} />
                  <button data-testid="dados-continue-btn" onClick={goIdentidade} className="flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-blue-700">Continuar<ArrowRight className="h-4 w-4" /></button>
                </div>
              </motion.div>
            )}

            {/* ETAPA 3 — Documento (análise por IA) */}
            {step === 2 && (
              <motion.div key="kyc" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }}>
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><ShieldCheck className="h-5 w-5 text-blue-600" />Verificação de Documento (IA)</h2>
                <p className="mt-1 text-sm text-slate-500">Envie uma foto nítida do seu documento oficial com CPF (RG ou CNH). Nossa IA lê o documento e confere o CPF com o que você cadastrou.</p>

                {!kycDone ? (
                  <div className="mt-4 space-y-3">
                    <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-slate-300 p-4 transition-colors hover:bg-slate-50">
                      <Upload className="h-5 w-5 text-slate-400" />
                      <div className="flex-1 text-sm">
                        <p className="font-semibold text-slate-700">Documento oficial (RG ou CNH)</p>
                        <p className="text-xs text-slate-400">{docName || "Toque para enviar a foto"}</p>
                      </div>
                      {docData && <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
                      <input data-testid="kyc-doc-input" type="file" accept="image/*" className="hidden" onChange={onDocFile} />
                    </label>

                    {kycResult && !kycResult.ok && (
                      <div data-testid="kyc-error-alert" className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 p-3.5 text-sm text-rose-700">
                        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                        <span>{kycResult.reason}</span>
                      </div>
                    )}

                    <button data-testid="kyc-verify-btn" onClick={verificarDocumento} disabled={kycProcessing || !docData} className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-3 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-60">
                      {kycProcessing ? <><Loader2 className="h-4 w-4 animate-spin" />Analisando documento com IA...</> : <><ShieldCheck className="h-4 w-4" />Verificar documento</>}
                    </button>
                    <p className="text-center text-[11px] text-slate-400">A foto é usada apenas para a verificação do CPF.</p>
                  </div>
                ) : (
                  <div data-testid="kyc-verified-seal" className="mt-4 flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                    <BadgeCheck className="h-7 w-7 shrink-0 text-emerald-600" />
                    <div className="text-sm">
                      <p className="font-bold text-emerald-800">Documento validado por IA</p>
                      <p className="text-emerald-700">{kycResult?.doc_type || "Documento"} · CPF confere com o cadastro · CRM {crm}-{uf}.</p>
                    </div>
                  </div>
                )}

                <div className="mt-5 flex justify-between">
                  <BackBtn onClick={() => setStep(1)} />
                  <button data-testid="finish-solo-btn" onClick={finalizar} disabled={!kycDone || submitting} className="flex items-center gap-2 rounded-xl bg-emerald-600 px-6 py-2.5 text-sm font-bold text-white shadow-[0_4px_14px_rgba(5,150,105,0.4)] transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400">
                    {submitting ? <><Loader2 className="h-4 w-4 animate-spin" />Criando conta...</> : <><CheckCircle2 className="h-4 w-4" />Concluir Cadastro e Acessar VeloClin</>}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <p className="mt-5 text-center text-xs text-slate-400">
          Já tem conta? <Link to="/login" className="font-semibold text-blue-600 hover:underline">Entrar</Link>
        </p>
      </div>
    </div>
  );
}

const Field = ({ label, testid, value, onChange, type = "text", placeholder = "" }) => (
  <div>
    <Label className="text-xs font-semibold text-slate-600">{label}</Label>
    <Input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1.5" />
  </div>
);

const BackBtn = ({ onClick }) => (
  <button onClick={onClick} className="flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50">
    <ArrowLeft className="h-4 w-4" />Voltar
  </button>
);
